import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Download, DollarSign, TrendingUp, FileText } from 'lucide-react';
import { reportsService } from '../../services/reportsService';
import { useAuth } from '../../context/AuthContext';

const MONTHS = [
  { value: '', label: 'All months' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const ProfitLoss = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canView = user && ['super_admin', 'finance_manager'].includes(user.role);

  const loadReport = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsService.getProfitLoss(month || undefined, year || undefined);
      setData(res.data || res);
    } catch (err) {
      setError(err.message || 'Unable to load Profit & Loss data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) loadReport();
  }, [month, year, canView]);

  const handleExport = async () => {
    try {
      await reportsService.exportProfitLoss(month || undefined, year || undefined);
    } catch (err) {
      alert(err.message || 'Export failed');
    }
  };

  if (!canView) {
    return (
      <div className="space-y-6 pb-10">
        <h1 className="text-xl font-bold">Profit & Loss</h1>
        <div className="rounded-card border border-gray-100 bg-yellow-50 p-6 text-yellow-800">
          You do not have access to this report.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 text-gray-900">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button onClick={() => navigate('/reports')} className="inline-flex items-center gap-2 text-sm text-primary font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to Reports
          </button>
          <h1 className="text-xl font-bold mt-2">Profit & Loss</h1>
          <p className="text-sm text-gray-500">Income, expenses, salaries, and profit margin.</p>
        </div>
        <button onClick={handleExport} className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark">
          <Download className="w-4 h-4" /> Export Excel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Revenue" value={data?.revenue} icon={<DollarSign className="w-5 h-5" />} />
        <SummaryCard label="Expenses" value={data?.expenses} icon={<TrendingUp className="w-5 h-5" />} />
        <SummaryCard label="Net Profit" value={data?.net_profit} icon={<FileText className="w-5 h-5" />} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Salaries" value={data?.salaries} icon={<DollarSign className="w-5 h-5" />} />
        <SummaryCard label="Commissions Paid" value={data?.commissions_paid} icon={<TrendingUp className="w-5 h-5" />} />
        <SummaryCard label="Profit Margin" value={data?.profit_margin_percent ? data.profit_margin_percent + '%' : '0%'} icon={<FileText className="w-5 h-5" />} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-card border border-gray-100 bg-white p-4">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Filter</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Month</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)} className="w-full rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-sm">
                {MONTHS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Year</label>
              <input type="number" min="2000" max="2099" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-full rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-sm" />
            </div>
          </div>
        </div>

        <div className="rounded-card border border-gray-100 bg-white p-4">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Current Period</h2>
          <p className="text-sm text-gray-500">{month ? MONTHS.find((m) => m.value === Number(month))?.label || 'Selected month' : 'All months'} {year}</p>
        </div>
      </div>

      {error && <div className="rounded-card border border-red-100 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary w-10 h-10" /></div>
      ) : (
        <div className="rounded-card border border-gray-100 bg-white p-6">
          <h2 className="text-sm font-bold text-gray-900 mb-4">Detail Summary</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <tbody className="divide-y divide-gray-100">
                {renderMetricRow('Revenue', data?.revenue)}
                {renderMetricRow('Expenses', data?.expenses)}
                {renderMetricRow('Salaries', data?.salaries)}
                {renderMetricRow('Commissions Paid', data?.commissions_paid)}
                {renderMetricRow('Total Costs', data?.total_costs)}
                {renderMetricRow('Net Profit', data?.net_profit)}
                {renderMetricRow('Profit Margin', data?.profit_margin_percent ? `${data.profit_margin_percent}%` : '0%')}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, icon }) => (
  <div className="rounded-card border border-gray-100 bg-white p-5 shadow-soft">
    <div className="flex items-center justify-between mb-3 text-gray-500">{label}{icon}</div>
    <div className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value ?? '0'}</div>
  </div>
);

const renderMetricRow = (label, value) => (
  <tr>
    <td className="px-4 py-3 text-gray-500 w-1/2">{label}</td>
    <td className="px-4 py-3 font-semibold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value ?? '0'}</td>
  </tr>
);

export default ProfitLoss;
